import React, { useState } from "react";
import { Modal } from "../common/Modal";
import { ProposalMilestone, MilestoneCategory } from "../../types";
import { useStaffDirectory } from "../../services/staffService";
import { Loader2 } from "lucide-react";

interface MilestoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  milestone?: ProposalMilestone | null;
  opportunityId: string;
  defaultTimezone?: string;
  onSave: (data: Partial<ProposalMilestone>) => Promise<void>;
}

interface MilestoneModalFormProps {
  milestone?: ProposalMilestone | null;
  defaultTimezone?: string;
  onClose: () => void;
  onSave: (data: Partial<ProposalMilestone>) => Promise<void>;
}

const MilestoneModalForm: React.FC<MilestoneModalFormProps> = ({
  milestone,
  defaultTimezone = "America/Chicago",
  onClose,
  onSave,
}) => {
  const { staff } = useStaffDirectory();

  const [title, setTitle] = useState(milestone?.title || "");
  const [description, setDescription] = useState(milestone?.description || "");
  const [targetDate, setTargetDate] = useState(milestone?.targetDate || "");
  const [targetTime, setTargetTime] = useState(milestone?.targetTime || (milestone ? "" : "15:00"));
  const [timezone, setTimezone] = useState(milestone?.timezone || defaultTimezone);
  const [category, setCategory] = useState<MilestoneCategory>(
    milestone?.category || "internal_gate",
  );
  const [ownerEmail, setOwnerEmail] = useState(milestone?.ownerEmail || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setErrorMessage("Please enter a milestone title.");
      return;
    }
    if (!targetDate) {
      setErrorMessage("Please specify a target date.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    const selectedStaff = staff.find((s) => s.email === ownerEmail);

    try {
      await onSave({
        title: title.trim(),
        description: description.trim() || undefined,
        targetDate,
        targetTime: targetTime.trim() || undefined,
        timezone,
        category,
        ownerEmail: ownerEmail || undefined,
        ownerName: selectedStaff ? selectedStaff.displayName : undefined,
      });
      onClose();
    } catch (err) {
      console.error("Failed to save milestone:", err);
      setErrorMessage(err instanceof Error ? err.message : "Failed to save milestone.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errorMessage && (
        <div className="rounded-lg bg-rose-50 p-2.5 text-xs text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
          {errorMessage}
        </div>
      )}

      <div>
        <label
          htmlFor="milestone-title"
          className="block text-xs font-semibold text-slate-700 dark:text-slate-300"
        >
          Milestone Title *
        </label>
        <input
          id="milestone-title"
          type="text"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. First Complete Draft Review Gate"
          className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label
            htmlFor="milestone-date"
            className="block text-xs font-semibold text-slate-700 dark:text-slate-300"
          >
            Target Date *
          </label>
          <input
            id="milestone-date"
            type="date"
            required
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
            Target Time (Optional)
          </label>
          <input
            type="time"
            value={targetTime}
            onChange={(e) => setTargetTime(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
            Category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as MilestoneCategory)}
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          >
            <option value="internal_gate">Internal Review Gate</option>
            <option value="external_deadline">External Client Deadline</option>
            <option value="client_event">Client Event / Meeting</option>
            <option value="custom">Custom Milestone</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
            Timezone
          </label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          >
            <option value="America/Chicago">America/Chicago (Central)</option>
            <option value="America/New_York">America/New_York (Eastern)</option>
            <option value="America/Denver">America/Denver (Mountain)</option>
            <option value="America/Los_Angeles">America/Los_Angeles (Pacific)</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
          Responsible Lead / Assignee
        </label>
        <select
          value={ownerEmail}
          onChange={(e) => setOwnerEmail(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
        >
          <option value="">Unassigned (Team Responsibility)</option>
          {staff.map((s) => (
            <option key={s.id} value={s.email}>
              {s.displayName} ({s.department || "Staff"})
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
          Description & Instructions (Optional)
        </label>
        <textarea
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Key deliverables, review expectations, or submission links..."
          className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
        <button
          type="button"
          onClick={onClose}
          className="btn-animate rounded-lg border border-slate-200 px-3.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238997] disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <span>{milestone ? "Save Changes" : "Create Milestone"}</span>
          )}
        </button>
      </div>
    </form>
  );
};

export const MilestoneModal: React.FC<MilestoneModalProps> = ({
  isOpen,
  onClose,
  milestone,
  defaultTimezone = "America/Chicago",
  onSave,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={milestone ? "Edit Response Milestone" : "Add Response Milestone"}
      description="Configure timeline milestones, review gates, and external client submission deadlines."
      maxWidth="lg"
    >
      {isOpen && (
        <MilestoneModalForm
          key={milestone ? milestone.id : "new"}
          milestone={milestone}
          defaultTimezone={defaultTimezone}
          onClose={onClose}
          onSave={onSave}
        />
      )}
    </Modal>
  );
};
